import React, { Fragment } from "react";

import { Box, Link, Typography } from "@material-ui/core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import PageTitle from "components/PageTitle";
import makeStyles from "hooks/makeStyles";
import useLocalize from "hooks/useLocalize";
import AppLogoIcon from "icons/AppLogoIcon";
import ForkKnifeIcon from "icons/ForkKnifeIcon";
import InformationIcon from "icons/InformationIcon";
import MapPinIcon from "icons/MapPinIcon";
import PhoneIcon from "icons/PhoneIcon";

const useStyles = makeStyles(theme => ({
	title: {
		display: "flex",
		flex: 1,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between"
	},
	titleText: {
		textTransform: "uppercase"
	},
	header: {},
	nameBox: {
		display: "flex",
		flex: 1,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "flex-start"
	},
	logo: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		maxHeight: 28,
		maxWidth: 28,
		marginRight: theme.spacing(1)
	},
	logoIcon: {
		alignItems: "center",
		justifyContent: "center",
		marginRight: theme.spacing(1)
	},
	fab: {},
	section: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		"&:hover": {
			textDecoration: "none"
		}
	},
	environment: {
		backgroundColor: "red",
		color: "#fff",
		borderRadius: 8,
		position: "absolute",
		top: 10,
		right: 10,
		padding: 5
	},
	icon: {
		height: theme.spacing(2),
		marginRight: theme.spacing(1)
	},
	text: {
		fontSize: 12,
		whiteSpace: "nowrap"
	},
	phoneIcon: {
		fontSize: 12,
		whiteSpace: "nowrap",
		height: 13,
		marginRight: theme.spacing(1),
		marginTop: "3px"
	},
	textLink: {
		fontSize: 12,
		whiteSpace: "nowrap",
		textDecoration: "none",
		color: theme.palette.text.primary
	},
	categoryText: {
		textTransform: "uppercase"
	}
}));

interface Props {
	name: string;
	isReceiptMode?: boolean;
	logoUrl?: string | null;
	categories: {
		id: string;
		name: string;
		localeNames: Record<string, string>;
	}[];
	address: string;
	mapUrl?: string | null;
	phoneNumber: string | null;
	displayPhoneNumber: string | null;
	environment: string | null | undefined;
}

const BusinessHeaderMobile = ({
	name,
	logoUrl,
	categories,
	address,
	mapUrl,
	phoneNumber,
	displayPhoneNumber,
	environment,
	isReceiptMode = false
}: Props) => {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const classes = useStyles();
	const navigate = useNavigate();

	const onCompanyInfoClicked = () => {
		navigate("#info");
	};
	const mapLinkUrl = mapUrl ? mapUrl : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
	return (
		<>
			<PageTitle businessName={name} subTitle={t("pageTitle")} />
			<Box className={classes.header}>
				<Box className={classes.nameBox}>
					{!isReceiptMode && (
						<Box className={classes.logo}>
							<Box className={classes.fab}>
								{logoUrl ? (
									<img src={logoUrl} className={classes.logo} />
								) : (
									<AppLogoIcon className={classes.logoIcon} />
								)}
							</Box>
						</Box>
					)}
					{/* <Box className={classes.title}> */}
					<Typography noWrap className={classes.titleText} variant="h2">
						{name}
					</Typography>
					{/* </Box> */}
				</Box>
				{/* {environment && <Box className={classes.environment}>{environment}</Box>} */}
				{!isReceiptMode && (
					<Box className={classes.section}>
						<ForkKnifeIcon className={classes.icon} />
						<Typography className={clsx(classes.text, classes.categoryText)}>
							{categories.map((category, index) => (
								<Fragment key={category.id}>
									{index === 0 ? null : <> &bull; </>}
									{localize(category.localeNames, category.name)}
								</Fragment>
							))}
						</Typography>
					</Box>
				)}
				<Link href={mapLinkUrl} target="_blank" className={classes.section}>
					<MapPinIcon className={classes.icon} />
					<Typography className={classes.textLink}>{address}</Typography>
				</Link>
				{isReceiptMode && displayPhoneNumber && (
					<Link href={`tel:${phoneNumber}`} className={classes.section}>
						<PhoneIcon className={classes.phoneIcon} />
						<Typography className={classes.textLink}>{displayPhoneNumber}</Typography>
					</Link>
				)}
				{!isReceiptMode && (
					<Box className={classes.section} onClick={onCompanyInfoClicked}>
						<InformationIcon className={classes.icon} />
						<Typography className={classes.text}>{t("tapForRestaurantInfo")}</Typography>
					</Box>
				)}
			</Box>
		</>
	);
};

export default BusinessHeaderMobile;
